import useQueryParams from 'hooks/useQueryParams'
import { useCallback, useMemo } from 'react'
import { replace } from 'lib/routing/appRouting'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import { EXP_CATEGORY_LUX_PLUS_ID, EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID } from 'constants/experience'

const SEARCH_FILTERS_BOUNDS = 'bounds'
const SEARCH_FILTERS_PRICE_LTE = 'priceLte'
const SEARCH_FILTERS_PRICE_GTE = 'priceGte'
const SEARCH_FILTERS_CATEGORY_CODES = 'categories'
const SEARCH_FILTERS_LUX_PLUS_EXCLUSIVE = 'luxPlusFeatures'
const SEARCH_FILTERS_COLLECTIONS = 'collections'
const SEARCH_FILTERS_DATE = 'date'
const SEARCH_FILTERS_MIN_RATING = 'minRating'
const SEARCH_FILTERS_FEATURED = 'featured'
const SEARCH_FILTERS_TRAVEL_STYLES = 'travelStyles'
const SEARCH_FILTERS_LE_EXCLUSIVE = 'leExclusive'
const SEARCH_FILTERS_CHECKIN_DATE = 'checkIn'
const SEARCH_FILTERS_CHECKOUT_DATE = 'checkOut'
const SEARCH_FILTERS_VIEW_PRICE_IN_POINTS = 'viewPriceInPoints'
const SEARCH_FILTERS_TIME_SLOTS = 'timeSlots'

export default function useExperiencesSearchFilters() {
  const appDispatch = useAppDispatch()
  const queryParameters = useQueryParams()
  const isExperiencesLuxPlusEnabled = useAppSelector((state) => getIsEnabledLuxPlusDiscountedExperiences(state))

  const replaceSearchUrl = (newUrlFilters: URLSearchParams) => {
    return appDispatch(replace({
      search: newUrlFilters.toString(),
    }))
  }

  const setSearchFilters = useCallback((newFilters: Experiences.SearchFilters) => {
    const newUrlFilters = convertSearchFiltersToUrl(newFilters, queryParameters)
    replaceSearchUrl(newUrlFilters)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParameters])

  const searchFilters = useMemo<Experiences.SearchFilters>(() => {
    const filters = extractSearchFiltersFromUrl(queryParameters)

    // If LuxPlus is not enabled, remove the LuxPlus category from the filters
    if (!isExperiencesLuxPlusEnabled) {
      filters.categoryCodes = filters.categoryCodes?.filter(code => ![EXP_CATEGORY_LUX_PLUS_ID, EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID].includes(code))
    }
    return filters
  }, [queryParameters, isExperiencesLuxPlusEnabled])

  const setCategoryCodesSearchFilter = useCallback((categoryCodes: Array<number>) => {
    const newUrlFilters = new URLSearchParams(queryParameters)
    if (categoryCodes?.length) {
      newUrlFilters.set(SEARCH_FILTERS_CATEGORY_CODES, categoryCodes.join(','))
    } else {
      newUrlFilters.delete(SEARCH_FILTERS_CATEGORY_CODES)
    }
    replaceSearchUrl(newUrlFilters)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParameters])

  const setPriceLteSearchFilter = useCallback((priceLte: number) => {
    const newUrlFilters = new URLSearchParams(queryParameters)
    if (Number.isFinite(priceLte)) {
      newUrlFilters.set(SEARCH_FILTERS_PRICE_LTE, String(priceLte))
    } else {
      newUrlFilters.delete(SEARCH_FILTERS_PRICE_LTE)
    }
    replaceSearchUrl(newUrlFilters)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParameters])

  const setPriceGteSearchFilter = useCallback((priceGte: number) => {
    const newUrlFilters = new URLSearchParams(queryParameters)
    if (Number.isFinite(priceGte)) {
      newUrlFilters.set(SEARCH_FILTERS_PRICE_GTE, String(priceGte))
    } else {
      newUrlFilters.delete(SEARCH_FILTERS_PRICE_GTE)
    }
    replaceSearchUrl(newUrlFilters)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParameters])

  const setBoundsSearchFilter = useCallback((bounds: string) => {
    const newUrlFilters = new URLSearchParams(queryParameters)
    if (bounds) {
      newUrlFilters.set(SEARCH_FILTERS_BOUNDS, bounds)
    } else {
      newUrlFilters.delete(SEARCH_FILTERS_BOUNDS)
    }
    replaceSearchUrl(newUrlFilters)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParameters])

  const setLuxPlusFeaturesFilter = useCallback((luxPlusFeatures: Array<App.LuxPlusFeatureFilter> | null) => {
    const newUrlFilters = new URLSearchParams(queryParameters)
    if (luxPlusFeatures) {
      newUrlFilters.set(SEARCH_FILTERS_LUX_PLUS_EXCLUSIVE, luxPlusFeatures.join(','))
    } else {
      newUrlFilters.delete(SEARCH_FILTERS_LUX_PLUS_EXCLUSIVE)
    }
    replaceSearchUrl(newUrlFilters)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParameters])

  return {
    searchFilters,
    setSearchFilters,
    setBoundsSearchFilter,
    setCategoryCodesSearchFilter,
    setPriceLteSearchFilter,
    setPriceGteSearchFilter,
    setLuxPlusFeaturesFilter,
  }
}

function extractSearchFiltersFromUrl(urlFilters: URLSearchParams): Experiences.SearchFilters {
  const searchFilters: Experiences.SearchFilters = {}

  if (urlFilters.has(SEARCH_FILTERS_CATEGORY_CODES)) {
    searchFilters.categoryCodes = urlFilters.get(SEARCH_FILTERS_CATEGORY_CODES)!.split(',').map(i => Number(i))
  }

  if (urlFilters.has(SEARCH_FILTERS_PRICE_LTE)) {
    searchFilters.priceLte = Number(urlFilters.get(SEARCH_FILTERS_PRICE_LTE))
  }

  if (urlFilters.has(SEARCH_FILTERS_PRICE_GTE)) {
    searchFilters.priceGte = Number(urlFilters.get(SEARCH_FILTERS_PRICE_GTE))
  }

  if (urlFilters.has(SEARCH_FILTERS_BOUNDS)) {
    searchFilters.bounds = urlFilters.get(SEARCH_FILTERS_BOUNDS)!
  }

  if (urlFilters.has(SEARCH_FILTERS_LUX_PLUS_EXCLUSIVE)) {
    searchFilters.luxPlusFeatures = urlFilters.get(SEARCH_FILTERS_LUX_PLUS_EXCLUSIVE)?.split(',').map(i => i as App.LuxPlusFeatureFilter)
  }

  if (urlFilters.has(SEARCH_FILTERS_COLLECTIONS)) {
    searchFilters.collections = urlFilters.get(SEARCH_FILTERS_COLLECTIONS)?.split(',')
  }

  if (urlFilters.has(SEARCH_FILTERS_LE_EXCLUSIVE)) {
    searchFilters.leExclusive = urlFilters.get(SEARCH_FILTERS_LE_EXCLUSIVE) === 'true'
  }

  if (urlFilters.has(SEARCH_FILTERS_DATE)) {
    searchFilters.date = urlFilters.get(SEARCH_FILTERS_DATE) as Experiences.DateFilter
  }

  if (urlFilters.has(SEARCH_FILTERS_MIN_RATING)) {
    searchFilters.minRating = Number(urlFilters.get(SEARCH_FILTERS_MIN_RATING))
  }

  if (urlFilters.has(SEARCH_FILTERS_FEATURED)) {
    searchFilters.featured = urlFilters.get(SEARCH_FILTERS_FEATURED)!.split(',') as Array<Experiences.FeaturedFilter>
  }

  if (urlFilters.has(SEARCH_FILTERS_TRAVEL_STYLES)) {
    searchFilters.travelStyles = urlFilters.get(SEARCH_FILTERS_TRAVEL_STYLES)!.split(',') as Array<Experiences.TravelStyleFilter>
  }

  if (urlFilters.has(SEARCH_FILTERS_CHECKIN_DATE)) {
    searchFilters.checkIn = urlFilters.get(SEARCH_FILTERS_CHECKIN_DATE)!
  }

  if (urlFilters.has(SEARCH_FILTERS_CHECKOUT_DATE)) {
    searchFilters.checkOut = urlFilters.get(SEARCH_FILTERS_CHECKOUT_DATE)!
  } else if (searchFilters.checkIn) {
    searchFilters.checkOut = searchFilters.checkIn
  }

  if (urlFilters.get(SEARCH_FILTERS_VIEW_PRICE_IN_POINTS) === 'true') {
    searchFilters.viewPriceInPoints = true
  }

  if (urlFilters.has(SEARCH_FILTERS_TIME_SLOTS)) {
    searchFilters.timeSlots = urlFilters.get(SEARCH_FILTERS_TIME_SLOTS)!.split(',') as Array<Experiences.TimeSlotFilter>
  }

  return searchFilters
}

function convertSearchFiltersToUrl(newFilters: Experiences.SearchFilters, currentSearchParams: URLSearchParams) {
  const searchParams = new URLSearchParams(currentSearchParams)

  if (Number.isFinite(newFilters.priceLte)) {
    searchParams.set(SEARCH_FILTERS_PRICE_LTE, String(newFilters.priceLte))
  } else {
    searchParams.delete(SEARCH_FILTERS_PRICE_LTE)
  }

  if (Number.isFinite(newFilters.priceGte)) {
    searchParams.set(SEARCH_FILTERS_PRICE_GTE, String(newFilters.priceGte))
  } else {
    searchParams.delete(SEARCH_FILTERS_PRICE_GTE)
  }

  if (newFilters.bounds) {
    searchParams.set(SEARCH_FILTERS_BOUNDS, newFilters.bounds)
  } else {
    searchParams.delete(SEARCH_FILTERS_BOUNDS)
  }

  if (newFilters.categoryCodes?.length) {
    searchParams.set(SEARCH_FILTERS_CATEGORY_CODES, newFilters.categoryCodes.join(','))
  } else {
    searchParams.delete(SEARCH_FILTERS_CATEGORY_CODES)
  }

  if (newFilters.collections?.length) {
    searchParams.set(SEARCH_FILTERS_COLLECTIONS, newFilters.collections.join(','))
  } else {
    searchParams.delete(SEARCH_FILTERS_COLLECTIONS)
  }

  if (newFilters.luxPlusFeatures) {
    searchParams.set(SEARCH_FILTERS_LUX_PLUS_EXCLUSIVE, newFilters.luxPlusFeatures.join(','))
  } else {
    searchParams.delete(SEARCH_FILTERS_LUX_PLUS_EXCLUSIVE)
  }

  if (newFilters.leExclusive) {
    searchParams.set(SEARCH_FILTERS_LE_EXCLUSIVE, 'true')
  } else {
    searchParams.delete(SEARCH_FILTERS_LE_EXCLUSIVE)
  }

  if (newFilters.date && newFilters.date !== 'anytime') {
    searchParams.set(SEARCH_FILTERS_DATE, newFilters.date)
  } else {
    searchParams.delete(SEARCH_FILTERS_DATE)
  }

  if (newFilters.minRating) {
    searchParams.set(SEARCH_FILTERS_MIN_RATING, String(newFilters.minRating))
  } else {
    searchParams.delete(SEARCH_FILTERS_MIN_RATING)
  }

  if (newFilters.featured?.length) {
    searchParams.set(SEARCH_FILTERS_FEATURED, newFilters.featured.join(','))
  } else {
    searchParams.delete(SEARCH_FILTERS_FEATURED)
  }

  if (newFilters.travelStyles?.length) {
    searchParams.set(SEARCH_FILTERS_TRAVEL_STYLES, newFilters.travelStyles.join(','))
  } else {
    searchParams.delete(SEARCH_FILTERS_TRAVEL_STYLES)
  }

  if (newFilters.checkIn) {
    searchParams.set(SEARCH_FILTERS_CHECKIN_DATE, newFilters.checkIn)
  } else {
    searchParams.delete(SEARCH_FILTERS_CHECKIN_DATE)
  }

  if (newFilters.checkOut) {
    searchParams.set(SEARCH_FILTERS_CHECKOUT_DATE, newFilters.checkOut)
  } else {
    searchParams.delete(SEARCH_FILTERS_CHECKOUT_DATE)
  }

  if (newFilters.viewPriceInPoints) {
    searchParams.set(SEARCH_FILTERS_VIEW_PRICE_IN_POINTS, 'true')
  } else {
    searchParams.delete(SEARCH_FILTERS_VIEW_PRICE_IN_POINTS)
  }

  if (newFilters.timeSlots?.length) {
    searchParams.set(SEARCH_FILTERS_TIME_SLOTS, newFilters.timeSlots.join(','))
  } else {
    searchParams.delete(SEARCH_FILTERS_TIME_SLOTS)
  }

  return searchParams
}
